import { useQuery } from "@powersync/tanstack-react-query"
import type { Species } from "@fieldbook/common/types"
import type { PowerSyncSpeciesRow } from "@/lib/powersync/schema"

function parseMetadata(raw: string | null): Species["metadata"] {
  if (!raw) return { florabaseProfileId: null }
  try {
    return JSON.parse(raw)
  } catch {
    return { florabaseProfileId: null }
  }
}

/**
 * Hook to fetch a single species by ID from local PowerSync SQLite.
 *
 * Uses ["species", "detail", speciesId] as the cache key.
 */
export const useSpecies = (speciesId?: string) => {
  const query = useQuery<PowerSyncSpeciesRow>({
    ...getSpeciesQueryOptions(speciesId),
    query: "SELECT * FROM species WHERE id = ?",
    parameters: [speciesId ?? ""],
    enabled: Boolean(speciesId),
  })

  const row = query.data?.[0]
  const species: Species | null = row
    ? {
        id: row.id,
        name: row.name ?? "",
        family: row.family ?? null,
        description: row.description ?? null,
        ala_guid: row.ala_guid ?? null,
        organisation_id: row.organisation_id ?? "",
        kingdom: row.kingdom ?? null,
        metadata: parseMetadata(row.metadata),
        created_at: row.created_at ?? "",
      }
    : null

  return {
    ...query,
    data: species,
  }
}

export const getSpeciesQueryOptions = (speciesId?: string) => ({
  queryKey: ["species", "detail", speciesId],
})
